import { useCallback, useMemo, useRef, useState } from "react";
import type { AskResult, ParseOverrides, SparqlResults } from "@nl2sparql/core";
import { useEngine } from "../../state/engine.tsx";
import { NlQueryBox } from "./NlQueryBox.tsx";
import { AnswerCard } from "./AnswerCard.tsx";
import { ExplainPanel, type DisambiguationPick } from "./ExplainPanel.tsx";
import { SparqlPanel } from "./SparqlPanel.tsx";

const EXAMPLES = [
  "capital of France",
  "films directed by Christopher Nolan",
  "how many children did Johann Sebastian Bach have",
  "largest country",
  "France's currency",
  "who directed Inception",
];

type Status = "idle" | "loading" | "done";

function answerSummary(r: AskResult): string {
  if (!r.ok || !r.answer) return r.error ? "no answer" : "no results";
  if (r.answer.kind === "count") return String(r.answer.value);
  if (r.answer.kind === "boolean") return r.answer.value ? "Yes" : "No";
  const first = r.answer.rows[0]?.map((c) => c.display).join(" · ") ?? "";
  return r.answer.rows.length > 1 ? `${first} (+${r.answer.rows.length - 1} more)` : first;
}

export function AskPage() {
  const { engine, sources, history, pushHistory } = useEngine();
  const [question, setQuestion] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [result, setResult] = useState<AskResult | null>(null);
  const [activeSourceId, setActiveSourceId] = useState("wikidata");
  const reqId = useRef(0);
  const lastQuestion = useRef("");
  const overrides = useRef<ParseOverrides>({});

  const activeLinker = useMemo(() => {
    const id = activeSourceId === "auto" ? "wikidata" : activeSourceId;
    const adapter = engine.getAdapter(id) ?? engine.getAdapter("wikidata");
    return adapter!.entityLinker;
  }, [engine, activeSourceId]);

  const runQuery = useCallback(
    async (q: string, ov: ParseOverrides) => {
      const trimmed = q.trim();
      if (!trimmed) return;
      const id = ++reqId.current;
      setStatus("loading");
      try {
        const r = await engine.ask(trimmed, { activeSourceId, overrides: ov });
        if (reqId.current !== id) return;
        setResult(r);
        setStatus("done");
        pushHistory({
          question: trimmed,
          sourceId: r.sourceId ?? activeSourceId,
          ok: r.ok,
          summary: answerSummary(r),
          at: Date.now(),
        });
      } catch (err) {
        if (reqId.current !== id) return;
        setResult({ question: trimmed, ok: false, error: err instanceof Error ? err.message : String(err), attempts: [] });
        setStatus("done");
      }
    },
    [engine, activeSourceId, pushHistory],
  );

  const ask = useCallback(
    (q: string) => {
      lastQuestion.current = q;
      overrides.current = {};
      setQuestion(q);
      void runQuery(q, {});
    },
    [runQuery],
  );

  // Guided disambiguation: re-run the last question with a pinned choice.
  const repick = useCallback(
    (pick: DisambiguationPick) => {
      const ov: ParseOverrides = { ...overrides.current };
      if (pick.kind === "entity") {
        ov.entityIri = pick.iri;
        ov.entityLabel = pick.label;
      } else {
        ov.propertyIri = pick.iri;
        ov.propertyLabel = pick.label;
      }
      overrides.current = ov;
      void runQuery(lastQuestion.current, ov);
    },
    [runQuery],
  );

  // Editable-SPARQL run: execute a raw query against the active endpoint source.
  const runSparql = useCallback(
    async (sparql: string): Promise<SparqlResults> => {
      const id = activeSourceId === "auto" ? "wikidata" : activeSourceId;
      const src = engine.registry.get(id) ?? engine.registry.defaultSource();
      if (src.kind === "local-store") {
        throw new Error("Editing runs against SPARQL endpoints; a local ontology runs via the structured query above.");
      }
      const adapter = engine.getAdapter(src.id);
      if (!adapter) throw new Error(`No executor for "${src.id}".`);
      return adapter.executor.run(sparql, src);
    },
    [engine, activeSourceId],
  );

  const sourceOptions = useMemo(() => {
    const opts = sources.map((s) => ({ id: s.id, label: s.label }));
    return sources.length > 1 ? [{ id: "auto", label: "Auto — best source" }, ...opts] : opts;
  }, [sources]);

  return (
    <div className="space-y-5">
      <section className="pt-2">
        <h1 className="text-2xl font-bold tracking-tight" style={{ color: "var(--nl2s-text)" }}>
          Ask a knowledge graph in plain language
        </h1>
        <p className="mt-1 text-sm" style={{ color: "var(--nl2s-text-muted)" }}>
          Deterministic-first: your question is matched to a template, grounded to real entities and
          properties, compiled to SPARQL, and executed. Query Wikidata or an ontology you upload.
        </p>
      </section>

      <NlQueryBox
        value={question}
        onChange={setQuestion}
        onSubmit={() => ask(question)}
        entityLinker={activeLinker}
        disabled={status === "loading"}
      />

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => ask(question)}
          disabled={status === "loading" || question.trim() === ""}
          className="rounded-lg px-4 py-2 text-sm font-semibold disabled:opacity-50"
          style={{ background: "var(--nl2s-accent)", color: "var(--nl2s-accent-contrast)" }}
        >
          {status === "loading" ? "Asking…" : "Ask"}
        </button>

        {sourceOptions.length > 1 ? (
          <label className="flex items-center gap-2 text-xs" style={{ color: "var(--nl2s-text-muted)" }}>
            Source
            <select
              value={activeSourceId}
              onChange={(e) => setActiveSourceId(e.target.value)}
              className="rounded-md border px-2 py-1 text-xs"
              style={{ borderColor: "var(--nl2s-border-strong)", background: "var(--nl2s-surface)", color: "var(--nl2s-text)" }}
            >
              {sourceOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <span className="text-xs" style={{ color: "var(--nl2s-text-muted)" }}>
          Enter to ask · Shift-Enter for a new line
        </span>
      </div>

      <div>
        <div className="mb-2 text-xs font-medium" style={{ color: "var(--nl2s-text-muted)" }}>
          Try:
        </div>
        <div className="flex flex-wrap gap-2">
          {EXAMPLES.map((ex) => (
            <button
              key={ex}
              type="button"
              onClick={() => ask(ex)}
              className="rounded-full border px-3 py-1 text-xs"
              style={{ borderColor: "var(--nl2s-border)", color: "var(--nl2s-text-muted)", background: "var(--nl2s-surface)" }}
            >
              {ex}
            </button>
          ))}
        </div>
      </div>

      {history.length > 0 ? (
        <div>
          <div className="mb-2 text-xs font-medium" style={{ color: "var(--nl2s-text-muted)" }}>
            Recent:
          </div>
          <div className="flex flex-wrap gap-2">
            {history.slice(0, 6).map((h, i) => (
              <button
                key={`${h.at}-${i}`}
                type="button"
                onClick={() => ask(h.question)}
                title={`${h.summary} · ${h.sourceId}`}
                className="rounded-full border px-3 py-1 text-xs"
                style={{ borderColor: "var(--nl2s-border)", color: "var(--nl2s-text)", background: "transparent" }}
              >
                {h.ok ? "" : "⚠ "}
                {h.question}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {status === "loading" ? (
        <div className="text-sm" style={{ color: "var(--nl2s-text-muted)" }}>
          Linking entities and running the query…
        </div>
      ) : null}

      {status === "done" && result ? (
        <div className="space-y-4">
          <AnswerCard result={result} />
          <div className="grid gap-4 md:grid-cols-2">
            <ExplainPanel result={result} onPick={repick} />
            <SparqlPanel result={result} onRun={runSparql} />
          </div>
        </div>
      ) : null}
    </div>
  );
}
